import React, { useMemo, useState } from 'react';
import { Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { claimsAPI } from '../services/apiService';
import OutlinedSelect, { statusTextClass } from './OutlinedSelect';
import {
  STATUS_OPTIONS,
  FACTOR_STATUS_FIELD,
  calculateFinalStatus,
  getActiveCaseFactors,
  resolveClaimFactorValues
} from '../services/pricingService';
import { generateClaimStatusChatMessage } from '../services/whatsappTemplates';
import chatService from '../services/chatService';
import { getStoredStaffUser } from '../utils/roles';
import {
  emptyFactorInstances,
  INSTANCE_TRACKED_FACTOR_KEYS,
  rollupInstanceStatuses
} from '../utils/factorInstances';

const parseJson = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const seedInstances = (claim) => {
  const pricing = parseJson(claim.pricing_data, {}) || {};
  const seeded = { ...emptyFactorInstances(), ...(pricing.factorInstances || {}) };
  INSTANCE_TRACKED_FACTOR_KEYS.forEach((key) => {
    if (!Array.isArray(seeded[key])) seeded[key] = [];
  });
  return seeded;
};

const ActivityStatusModal = ({ claim, member, onClose, onSuccess }) => {
  const pricingData = useMemo(() => parseJson(claim.pricing_data, {}) || {}, [claim.pricing_data]);
  const factorValues = useMemo(() => resolveClaimFactorValues(claim), [claim]);

  // Only case factors with count > 0 — labels identical to Case Factors
  const inScopeFactors = useMemo(
    () => getActiveCaseFactors(factorValues),
    [factorValues]
  );
  const inScopeLabels = useMemo(
    () => inScopeFactors.map((f) => f.label),
    [inScopeFactors]
  );

  const [statuses, setStatuses] = useState(() => {
    const next = {};
    const values = resolveClaimFactorValues(claim);
    const fromPricing = parseJson(claim.pricing_data, {})?.activityStatuses || {};
    getActiveCaseFactors(values).forEach((factor) => {
      const field = FACTOR_STATUS_FIELD[factor.key];
      if (field) next[field] = claim[field] || fromPricing[field] || 'Not Started';
    });
    return next;
  });
  const [factorInstances, setFactorInstances] = useState(() => seedInstances(claim));
  const [saving, setSaving] = useState(false);

  const previousSnapshot = useMemo(() => {
    const fromPricing = pricingData.activityStatuses || {};
    return {
      statuses: Object.fromEntries(
        inScopeFactors.map((factor) => {
          const field = FACTOR_STATUS_FIELD[factor.key];
          return [field, claim[field] || fromPricing[field] || 'Not Started'];
        }).filter(([field]) => field)
      ),
      factorInstances: seedInstances(claim)
    };
  }, [claim, inScopeFactors, pricingData.activityStatuses]);

  const finalStatus = useMemo(
    () => calculateFinalStatus(inScopeFactors, statuses),
    [inScopeFactors, statuses]
  );

  const setInstanceStatus = (factorKey, instanceKey, status) => {
    const nextList = (factorInstances[factorKey] || []).map((inst) => (
      inst.key === instanceKey ? { ...inst, status } : inst
    ));
    setFactorInstances((prev) => ({ ...prev, [factorKey]: nextList }));
    const field = FACTOR_STATUS_FIELD[factorKey];
    if (field) {
      setStatuses((s) => ({ ...s, [field]: rollupInstanceStatuses(nextList) }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalizedInstances = { ...emptyFactorInstances(), ...factorInstances };
      const nextStatuses = { ...statuses };

      INSTANCE_TRACKED_FACTOR_KEYS.forEach((key) => {
        const list = normalizedInstances[key] || [];
        if (list.length >= 1) {
          const field = FACTOR_STATUS_FIELD[key];
          if (field) nextStatuses[field] = rollupInstanceStatuses(list);
        }
      });

      const nextFinal = calculateFinalStatus(inScopeFactors, nextStatuses);
      const nextPricing = {
        ...pricingData,
        factorValues,
        factorInstances: normalizedInstances,
        activityStatuses: nextStatuses,
        requirements: inScopeLabels
      };

      const payload = {
        ...claim,
        ...nextStatuses,
        requirements: inScopeLabels,
        requirement: inScopeLabels.join(', '),
        pricing_data: nextPricing,
        final_status: nextFinal,
        status: nextFinal,
        updated_at: new Date().toISOString()
      };
      await claimsAPI.update(claim.id, payload);

      const memberId = member?.id || claim.member_id;
      if (memberId) {
        try {
          const memberForMsg = member || {
            id: memberId,
            name: claim.member_name || 'Member',
            uan: claim.member_uan || ''
          };
          const { subject, message } = generateClaimStatusChatMessage({
            member: memberForMsg,
            claimId: claim.id,
            previous: previousSnapshot,
            next: { statuses: nextStatuses, factorInstances: normalizedInstances },
            requirements: inScopeLabels,
            finalStatus: nextFinal
          });
          const staff = getStoredStaffUser();
          await chatService.addAdminReply(
            memberId,
            `${subject}\n\n${message}`,
            {
              adminName: staff?.username || 'Admin',
              adminUserId: staff?.id || null
            }
          );
          toast.success('Status saved and sent to member chat');
        } catch (chatError) {
          console.error('Chat notify failed:', chatError);
          toast.success('Status saved (chat notify failed)');
        }
      } else {
        toast.success('Claim status updated');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update claim status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-5">
      <div
        className="bg-white rounded-xl shadow-2xl w-full flex flex-col"
        style={{ width: 'min(1100px, 96vw)', height: 'min(720px, 92vh)' }}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Update Claim Status</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Case factors with count > 0 · Claim #{claim.id}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-5 sm:p-6">
          {inScopeFactors.length === 0 ? (
            <p className="text-sm text-slate-500">
              No case factors with count > 0. Open Case Study and set Case Factors first.
            </p>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-6">
                {inScopeFactors.map((factor) => {
                  const field = FACTOR_STATUS_FIELD[factor.key];
                  if (!field) return null;
                  const instances = factorInstances[factor.key] || [];
                  if (instances.length >= 1) return null;

                  return (
                    <OutlinedSelect
                      key={factor.key}
                      label={factor.label}
                      value={statuses[field] || 'Not Started'}
                      options={STATUS_OPTIONS}
                      statusTone
                      onChange={(val) => setStatuses((prev) => ({ ...prev, [field]: val }))}
                    />
                  );
                })}
              </div>

              {inScopeFactors.map((factor) => {
                const instances = factorInstances[factor.key] || [];
                if (instances.length < 1) return null;
                const field = FACTOR_STATUS_FIELD[factor.key];
                const countHint = Math.max(0, parseFloat(factorValues[factor.key]) || 0);

                return (
                  <div key={`inst-${factor.key}`} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <h4 className="text-sm font-semibold text-slate-900">{factor.label}</h4>
                      <p className="text-xs text-slate-500">
                        {instances.length} instance{instances.length === 1 ? '' : 's'}
                        {countHint ? ` · factor count ${countHint}` : ''}
                        {' · '}roll-up:{' '}
                        <span className={statusTextClass(statuses[field])}>
                          {statuses[field] || 'Not Started'}
                        </span>
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {instances.map((inst) => (
                        <OutlinedSelect
                          key={inst.key}
                          label={inst.label || inst.company_name || inst.member_id || 'Instance'}
                          value={inst.status || 'Not Started'}
                          options={STATUS_OPTIONS}
                          statusTone
                          onChange={(val) => setInstanceStatus(factor.key, inst.key, val)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 shrink-0 bg-slate-50/80">
          <p className="text-sm text-left">
            Final status:{' '}
            <span className={`font-semibold ${statusTextClass(finalStatus)}`}>
              {finalStatus}
            </span>
          </p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              type="button"
              disabled={saving || inScopeFactors.length === 0}
              onClick={handleSave}
              className="btn-primary inline-flex items-center disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityStatusModal;
